import { cn } from "cn";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button/button";
import { PriceInput } from "@/components/ui/input/price-input";
import { Slider } from "@/components/ui/slider/slider";
import { formatCurrencyUSD } from "@/libs/format-number";

import {
  ActiveFilterChip,
  EMPTY_VALUE_LABEL,
} from "../_components/active-filter-chip";
import { useFilters } from "../_hooks/use-filters";
import type { PriceRangeValue } from "../codecs";
import { LOWER_BOUND_OPERATORS, UPPER_BOUND_OPERATORS } from "../operators";
import type { FilterField } from "../types";

const DEFAULT_BOUNDS = { min: 0, max: 10_000 };

function formatPrice(value: number): string {
  const hasCents = !Number.isInteger(value);
  return formatCurrencyUSD(value, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function toWholeDollar(value: number, min: number, max: number): number {
  return Math.round(clamp(value, min, max));
}

function parseDraft(value: string, fallback: number): number {
  if (value.trim() === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Which bound(s) the active operator reads — the rest of the widget follows. */
type BoundMode = "range" | "min" | "max";

function formatRangeLabel(
  value: PriceRangeValue | undefined,
  mode: BoundMode,
): string {
  if (!value) return EMPTY_VALUE_LABEL;
  if (mode === "min") return formatPrice(value.min);
  if (mode === "max") return formatPrice(value.max);
  return `${formatPrice(value.min)} - ${formatPrice(value.max)}`;
}

/**
 * Dollar price filter: a dual-thumb slider with min/max inputs for the range
 * operators, or a single bound for the comparison ones. URL values are whole
 * dollars; the `priceRangeCodec` converts them to cents for the API.
 *
 * Both bounds stay in the URL value even when the active operator reads only
 * one, so switching operators never discards what the user already picked.
 */
export const PriceRangeFilter = ({ field }: { field: FilterField }) => {
  const { filters, filtersOrder, operatorFor, modify, clear } = useFilters();
  const bounds = field.priceRangeBounds ?? DEFAULT_BOUNDS;
  const key = field.key;
  const applied = (filters as Record<string, PriceRangeValue | "">)[key];
  const appliedValue =
    applied && typeof applied === "object" ? applied : undefined;

  const operator = operatorFor(field);
  const mode: BoundMode = LOWER_BOUND_OPERATORS.includes(operator)
    ? "min"
    : UPPER_BOUND_OPERATORS.includes(operator)
      ? "max"
      : "range";

  const [open, setOpen] = useState(
    () => !appliedValue && filtersOrder.at(-1) === key,
  );

  const initialDraft = useMemo(
    () => ({
      min: toWholeDollar(
        appliedValue?.min ?? bounds.min,
        bounds.min,
        bounds.max,
      ),
      max: toWholeDollar(
        appliedValue?.max ?? bounds.max,
        bounds.min,
        bounds.max,
      ),
    }),
    [appliedValue?.min, appliedValue?.max, bounds.min, bounds.max],
  );

  const [draftMin, setDraftMin] = useState(String(initialDraft.min));
  const [draftMax, setDraftMax] = useState(String(initialDraft.max));
  const [sliderValue, setSliderValue] = useState<[number, number]>([
    initialDraft.min,
    initialDraft.max,
  ]);

  useEffect(() => {
    if (!open) return;
    setDraftMin(String(initialDraft.min));
    setDraftMax(String(initialDraft.max));
    setSliderValue([initialDraft.min, initialDraft.max]);
  }, [open, initialDraft.min, initialDraft.max]);

  // In a single-bound mode the two drafts are independent — clamping max up to
  // min would silently move the bound the operator doesn't even read.
  const syncDraft = (min: number, max: number) => {
    const nextMin = toWholeDollar(min, bounds.min, bounds.max);
    const nextMax = toWholeDollar(
      mode === "range" ? Math.max(max, nextMin) : max,
      bounds.min,
      bounds.max,
    );
    setDraftMin(String(nextMin));
    setDraftMax(String(nextMax));
    setSliderValue([nextMin, nextMax]);
  };

  const handleApply = () => {
    const min = parseDraft(draftMin, bounds.min);
    const max = parseDraft(draftMax, bounds.max);
    const nextMin = toWholeDollar(min, bounds.min, bounds.max);
    const nextMax = toWholeDollar(
      mode === "range" ? Math.max(max, nextMin) : max,
      bounds.min,
      bounds.max,
    );
    modify({
      [key]: {
        min: nextMin,
        max: nextMax,
      },
    });
    setOpen(false);
  };

  return (
    <ActiveFilterChip
      field={field}
      valueLabel={formatRangeLabel(appliedValue, mode)}
      open={open}
      onOpenChange={setOpen}
      onRemove={() => clear(key)}
    >
      <div
        className={cn(
          /* sorted */
          "flex flex-col gap-4",
          "bg-bg-surface-elevate-low rounded-xl",
          "p-4",
          field.popoverWidthClassName ?? "w-72",
        )}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-xs font-medium text-text-dark-high">
            <span>
              {formatPrice(mode === "max" ? bounds.min : sliderValue[0])}
            </span>
            <span>
              {formatPrice(mode === "min" ? bounds.max : sliderValue[1])}
            </span>
          </div>
          <Slider
            min={bounds.min}
            max={bounds.max}
            step={1}
            value={
              mode === "min"
                ? [sliderValue[0]]
                : mode === "max"
                  ? [sliderValue[1]]
                  : sliderValue
            }
            onValueChange={(values) => {
              if (mode === "min") {
                syncDraft(values[0], sliderValue[1]);
                return;
              }
              if (mode === "max") {
                syncDraft(sliderValue[0], values[0]);
                return;
              }
              const [min, max] = values as [number, number];
              syncDraft(min, max);
            }}
          />
        </div>

        <div className="flex items-center gap-2">
          {mode !== "max" && (
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-text-dark-high">
              Minimum
              <PriceInput
                prefix="$"
                value={draftMin}
                aria-label="Minimum price"
                onChange={(value) => {
                  const next = String(value);
                  if (/^\d*$/.test(next)) setDraftMin(next);
                }}
                onBlur={() =>
                  syncDraft(parseDraft(draftMin, bounds.min), sliderValue[1])
                }
                className="h-9"
              />
            </label>
          )}
          {mode !== "min" && (
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-text-dark-high">
              Maximum
              <PriceInput
                prefix="$"
                value={draftMax}
                aria-label="Maximum price"
                onChange={(value) => {
                  const next = String(value);
                  if (/^\d*$/.test(next)) setDraftMax(next);
                }}
                onBlur={() =>
                  syncDraft(sliderValue[0], parseDraft(draftMax, bounds.max))
                }
                className="h-9"
              />
            </label>
          )}
        </div>

        <Button
          kind="soft"
          variant="neutral"
          type="button"
          size="sm"
          className="w-full"
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </ActiveFilterChip>
  );
};
